import {useCallback, useEffect, useMemo, useState} from "react";
import {isEqual} from "lodash";
import {RoutePlanningRoute} from "../../../generated/graphql";
import {useRoutePlanningContext} from "../../../providers/RoutePlanningProvider";
import {metersToMiles} from "../../../utils/General";
import {AgStop} from "../../common/types";
import {RouteEtasUpdate} from "../GetRouteWithEtas.service";
import {RowData} from "../types/types";
import {toVehicleId} from "../utils/RoutePlanning.utils";

interface UseRoutePlanningRowsArgs {
  routes: RoutePlanningRoute[] | null;
  driverColorMap: Map<string, string>;
}

/**
 * DP-3147: pure driving seconds for a route straight off the optimizer solution.
 *
 * `transport_time` is the route-level summary and the preferred source, but it is nullable, and
 * `Number(undefined ?? 0)` would render a 0h 0m transit time — silently pricing the calculator's
 * $/hour line at zero. Activity `driving_time` is CUMULATIVE, so the last activity's value is the
 * whole-route driving total (verified identical to `transport_time` across the GraphHopper
 * fixtures) and includes the legs to and from the driver's start/end locations. It is also the
 * exact series the route-planning lambda builds its ETAs from, so the fallback cannot disagree
 * with the ETAs on screen.
 *
 * Number() is required either way — both fields are the Long scalar, typed `any` by codegen, so a
 * string value would otherwise propagate as NaN with no type error.
 */
/**
 * Owns the route grid's row state for Itinerary Planning: it builds the immutable
 * `originalRowData` baseline + the editable `workingRowData` from the optimizer
 * result, derives whether the plan has been modified, reconciles rows that the user
 * reverts back to their original order, and applies the map's recalculated
 * ETAs/miles back into the rows.
 *
 * Centralizing this here keeps the (many) writers of `workingRowData` — the initial
 * build, drag/drop + dismiss from the grid, the async getRouteWithEtas write-backs,
 * the restore reconciliation, and reset — in one cohesive place.
 */
export const useRoutePlanningRows = ({routes, driverColorMap}: UseRoutePlanningRowsArgs) => {
  const {originalResult, selectedJobs, selectedDrivers, manifestStopsMap} = useRoutePlanningContext();

  const [originalRowData, setOriginalRowData] = useState<RowData[] | null>(null);
  const [workingRowData, setWorkingRowData] = useState<RowData[] | null>(null);
  const [unassignedJobIds, setUnassignedJobIds] = useState<number[]>([]);
  // DP-3230: reasons typed in the map skill check, by job id; sent per route with Create Routes.
  const [skillOverrideReasons, setSkillOverrideReasons] = useState<Record<number, string>>({});

  // Build originalRowData and workingRowData from originalResult.
  // Wait for driverColorMap to be populated (from routes + untouched manifests).
  useEffect(() => {
    if (originalRowData !== null || originalResult === null || driverColorMap.size === 0) return;

    // Index manifest stops by jobStopId for fast lookup (from Step 1 stored data)
    const manifestStopsByJobStopId = new Map<number, (typeof allManifestStops)[0]>();
    const allManifestStops = Object.values(manifestStopsMap ?? {}).flat();
    for (const s of allManifestStops) {
      manifestStopsByJobStopId.set(s.jobStopId, s);
    }

    const rowData: RowData[] = originalResult.assignmentDetails
      .map((assignment, idx) => {
        const driverId = assignment.submittedDriver.driverId;
        const expectedVehicleId = toVehicleId(assignment.submittedDriver);
        // DP-3175: Match by manifestDriverId when present, not just driverId.
        // The same physical driver can appear as both a new driver and an
        // existing manifest — matching by driverId alone picks the wrong entry.
        const driver = selectedDrivers.find((d) =>
          assignment.submittedDriver.manifestDriverId
            ? d.manifestDriverId === assignment.submittedDriver.manifestDriverId
            : d.driverId === driverId && !d.manifestDriverId
        );
        if (!driver) return null;
        const route = originalResult.result.solution?.routes?.find((r) => r.vehicle_id === expectedVehicleId);
        const lastStop = assignment.optimizationEtas.at(-1);
        const assignedJobsForDriver = selectedJobs.filter((job) => assignment.jobIds.includes(job.jobId));

        const endTime = assignment.endDateTime ? new Date(assignment.endDateTime) : undefined;
        const endTimeZone =
          assignedJobsForDriver.flatMap((job) => job.stops).find((stop) => stop.jobStopId === lastStop?.jobStopId)
            ?.timeZone ??
          // DP-3175: fallback to manifest stop timezone for untouched manifests
          (lastStop ? manifestStopsByJobStopId.get(lastStop.jobStopId)?.timeZone : undefined) ??
          undefined;
        const duration = endTime ? endTime.getTime() - new Date(assignment.submittedDriver.startTime!).getTime() : 0;
        const miles = metersToMiles(route?.distance ?? 0);
        const assignedStops = assignment.sequences.map((jobStopId, index) => {
          // Try new jobs first
          const parentJob = assignedJobsForDriver.find((job) => job.stops.some((s) => s.jobStopId === jobStopId));
          if (parentJob) {
            const stop = parentJob.stops.find((s) => s.jobStopId === jobStopId);
            const eta = assignment.optimizationEtas.find((e) => e.jobStopId === jobStopId);
            return {
              ...stop,
              ...eta,
              manifestSequence: index + 1,
              tenantId: parentJob.tenantId,
              job: parentJob,
              order: parentJob.order
            } as AgStop;
          }
          // Fallback to manifest stops stored from Step 1
          const manifestStop = manifestStopsByJobStopId.get(jobStopId);
          if (manifestStop) {
            const eta = assignment.optimizationEtas.find((e) => e.jobStopId === jobStopId);
            return {
              ...manifestStop,
              ...eta,
              tenantId: "",
              sequence: manifestStop.manifestSequence,
              manifestSequence: index + 1
            } as unknown as AgStop;
          }
          return null;
        });

        const startActivity = route?.activities?.find((a) => a.type === "start");
        const endActivity = route?.activities?.find((a) => a.type === "end");
        // DP-3175: For untouched manifests (no GH route), fall back to submitted
        // driver's start position so the map can call getRouteWithEtas.
        const driverStartPos = assignment.submittedDriver.startAddressPosition;

        return {
          ...driver,
          routeId: `route-${idx}`,
          endTime,
          endTimeZone,
          duration,
          miles,
          assignedStops: assignedStops.filter(Boolean) as AgStop[],
          routeColor: driverColorMap.get(route?.vehicle_id ?? expectedVehicleId) ?? "#4B5563",
          isVisible: true,
          startLat: startActivity?.address?.lat ?? driverStartPos?.latitude ?? undefined,
          startLng: startActivity?.address?.lon ?? driverStartPos?.longitude ?? undefined,
          endLat: endActivity?.address?.lat ?? undefined,
          endLng: endActivity?.address?.lon ?? undefined,
          startDateTime: assignment.submittedDriver.startTime ?? undefined
        };
      })
      .filter(Boolean) as RowData[];

    setOriginalRowData(rowData);
    setWorkingRowData(rowData);
  }, [driverColorMap, originalResult, originalRowData, routes, selectedDrivers, selectedJobs]);

  // Derive isModified by comparing working vs original
  const isModified = useMemo(() => {
    if (!originalRowData || !workingRowData) return false;
    if (unassignedJobIds.length > 0) return true;
    if (originalRowData.length !== workingRowData.length) return true;
    return !isEqual(
      originalRowData.map((r) => ({driverId: r.driverId, stopIds: r.assignedStops.map((s) => s.jobStopId)})),
      workingRowData.map((r) => ({driverId: r.driverId, stopIds: r.assignedStops.map((s) => s.jobStopId)}))
    );
  }, [originalRowData, workingRowData, unassignedJobIds]);

  // Restore original row data for routes whose sequence reverted back to original.
  // Modification handlers clear endTime/duration/miles, but if the user drags stops back
  // to the original order, no route fetch happens — so we must restore from originalRowData.
  useEffect(() => {
    if (!originalRowData || !workingRowData) return;

    // DP-3219: Manifest routes with stripped points get recalculated via
    // getRouteWithEtas. Don't restore those — their GH-computed original
    // values would overwrite the ETA Lambda values from getRouteWithEtas.
    const strippedManifestRouteIds = new Set<string>();
    const routes = originalResult?.result?.solution?.routes ?? [];
    routes.forEach((route, i) => {
      if (route.vehicle_id?.startsWith("manifest:") && (!route.points || route.points.length === 0)) {
        strippedManifestRouteIds.add(`route-${i}`);
      }
    });

    let needsRestore = false;
    const restored = workingRowData.map((row) => {
      const original = originalRowData.find((r) => r.routeId === row.routeId);
      if (!original) return row;

      // DP-3219: Skip restore for manifest routes being recalculated
      if (strippedManifestRouteIds.has(row.routeId)) return row;

      const currentStopIds = row.assignedStops.map((s) => s.jobStopId);
      const originalStopIds = original.assignedStops.map((s) => s.jobStopId);

      const sequenceMatches =
        currentStopIds.length === originalStopIds.length && currentStopIds.every((id, i) => id === originalStopIds[i]);

      const originalIsCleanBaseline = original.endTime != null;

      if (
        row.driverId === original.driverId &&
        originalIsCleanBaseline &&
        sequenceMatches &&
        (row.miles !== original.miles || row.duration !== original.duration)
      ) {
        needsRestore = true;
        return original;
      }
      return row;
    });

    if (needsRestore) {
      setWorkingRowData(restored);
    }
  }, [workingRowData, originalRowData, originalResult]);

  const handleRouteEtasUpdate = useCallback((routeId: string, update: RouteEtasUpdate) => {
    setWorkingRowData((prev) => {
      if (!prev) return prev;
      return prev.map((row) => {
        if (row.routeId !== routeId) return row;
        const assignedStops = row.assignedStops.map((stop) => {
          const eta = update.etaByJobStopId.get(stop.jobStopId);
          if (!eta) return stop;
          const etaSource = update.etaSourceByJobStopId.get(stop.jobStopId);
          return etaSource ? {...stop, eta, etaSource} : {...stop, eta};
        });
        const endTime = update.endDateTime ? new Date(update.endDateTime) : row.endTime;
        const endTimeZone = assignedStops.at(-1)?.timeZone ?? row.endTimeZone;
        const startMs = row.startDateTime
          ? new Date(row.startDateTime).getTime()
          : row.startTime instanceof Date
          ? row.startTime.getTime()
          : undefined;
        const duration = endTime && startMs != null ? endTime.getTime() - startMs : row.duration;
        return {...row, assignedStops, endTime, endTimeZone, duration, miles: update.miles};
      });
    });
  }, []);

  // Reset the editable rows back to the original baseline.
  const resetRows = useCallback(() => {
    if (originalRowData) {
      setWorkingRowData(originalRowData);
      setUnassignedJobIds([]);
      setSkillOverrideReasons({});
    }
  }, [originalRowData]);

  return {
    originalRowData,
    workingRowData,
    setWorkingRowData,
    unassignedJobIds,
    setUnassignedJobIds,
    skillOverrideReasons,
    setSkillOverrideReasons,
    isModified,
    handleRouteEtasUpdate,
    resetRows
  };
};
